import Dexie, { type Table } from 'dexie';
import { NetworkError, ProblemError, type AccountEntry, type HttpClient, type Me, type Tokens } from '@/model';
import { object } from './decode';

export interface AccountRegistration { account_id: string; base_url: string; me: Me; tokens: Tokens; login_id?: string }
export interface AccountIdentity { account_id: string; base_url: string; me: Me; badge?: number; last_active_at?: number; expired?: boolean }

export class AccountsRegistry extends Dexie {
  accounts!: Table<AccountRegistration, string>;
  active!: Table<{ base_url: string; account_id: string }, string>;
  identities!: Table<AccountIdentity, string>;
  deletions!: Table<{ account_id: string; base_url: string; login_id: string; completed: boolean }, string>;
  constructor() {
    super('hylo-web-accounts');
    this.version(1).stores({ accounts: 'account_id,base_url', active: 'base_url' });
    this.version(2).stores({ identities: 'account_id,base_url', deletions: 'account_id,base_url' });
    // Optional metadata uses the existing records; shipped schema versions remain frozen.
  }
  async read(baseUrl: string) {
    return this.transaction('r', this.accounts, this.identities, async () => ({
      registrations: await this.accounts.where('base_url').equals(baseUrl).toArray(),
      identities: await this.identities.where('base_url').equals(baseUrl).toArray(),
    }));
  }
}

export function accountEntries({ registrations, identities }: Awaited<ReturnType<AccountsRegistry['read']>>): AccountEntry[] {
  const signedIn = new Map(registrations.map(entry => [entry.account_id, entry]));
  const known = new Map(identities.map(entry => [entry.account_id, entry]));
  for (const registration of registrations) if (!known.has(registration.account_id)) known.set(registration.account_id, registration);
  return [...known.values()].map(entry => {
    const account = entry.me.account, metadata = entry as AccountIdentity;
    return { id: account.id, handle: account.handle, displayName: account.display_name,
      avatar: { mediaId: account.avatar_media_id, name: account.display_name, seed: account.id, shape: 'person' as const },
      badge: signedIn.has(account.id) ? Math.max(0, metadata.badge ?? 0) : 0,
      status: signedIn.has(account.id) ? 'signedIn' as const : metadata.expired ? 'expired' as const : 'signedOut' as const };
  }).sort((a, b) => (a.displayName || a.handle).localeCompare(b.displayName || b.handle, undefined, { numeric: true, sensitivity: 'base' }) || a.handle.localeCompare(b.handle) || a.id.localeCompare(b.id));
}

export function decodeTokens(value: unknown): Tokens {
  const t = object(value);
  if (typeof t.access_token !== 'string' || typeof t.refresh_token !== 'string' || typeof t.access_expires_at !== 'string' || typeof t.refresh_expires_at !== 'string') throw new NetworkError('Invalid session response');
  return t as unknown as Tokens;
}

/** All callers, including closed-account badge clients, share the same cross-tab refresh lock. */
export async function rotateRegistration(registry: AccountsRegistry, api: HttpClient, deviceId: string, registration: AccountRegistration, current: () => boolean): Promise<AccountRegistration> {
  const run = async () => {
    const stored = await registry.accounts.get(registration.account_id);
    if (!current()) throw new NetworkError('This account session has ended');
    if (!stored || stored.base_url !== registration.base_url || stored.login_id !== registration.login_id) throw new ProblemError({ status: 401, code: 'session_revoked' });
    if (stored.tokens.refresh_token !== registration.tokens.refresh_token && Date.parse(stored.tokens.access_expires_at) > Date.now() + 30000) return stored;
    const response = object(await api.post('/v1/auth/refresh', { refresh_token: stored.tokens.refresh_token, device_id: deviceId }, { anonymous: true }));
    const next = { ...stored, tokens: decodeTokens(response.tokens) };
    const saved = await registry.transaction('rw', registry.accounts, async () => {
      const held = await registry.accounts.get(stored.account_id);
      // A switch retires callers, not this account's credential family. Keep a successful rotation
      // durable unless that login was removed/replaced while the request was in flight.
      if (held?.base_url !== registration.base_url || held.login_id !== registration.login_id) return false;
      await registry.accounts.put({ ...held, tokens: next.tokens }); return true;
    });
    if (!current()) throw new NetworkError('This account session has ended');
    if (!saved) throw new ProblemError({ status: 401, code: 'session_revoked' });
    return next;
  };
  return globalThis.navigator?.locks ? navigator.locks.request(`hylo.web.refresh:${registration.account_id}`, run) : run();
}
